import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  getBranch, getBranchStats,
  approveBranch, suspendBranch, reactivateBranch, archiveBranch,
} from '../../services/branchService'
import type { BranchResponse, BranchStats } from '../../types'
import { BranchStatusBadge } from '../BranchesPage/BranchesPage'
import BranchFormModal from '../../components/BranchForm/BranchFormModal'
import BranchSmsCard from '../../components/BranchSmsConfig/BranchSmsCard'
import BranchLogoCard from '../../components/BranchLogo/BranchLogoCard'
import DealerUsersCard from '../../components/DealerUsers/DealerUsersCard'
import './BranchDetailPage.css'

export default function BranchDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [branch, setBranch] = useState<BranchResponse | null>(null)
  const [stats, setStats] = useState<BranchStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null)
  const [showEdit, setShowEdit] = useState(false)
  const [suspendReason, setSuspendReason] = useState('')
  const [showSuspendForm, setShowSuspendForm] = useState(false)

  const load = useCallback(async () => {
    if (!id) return
    try {
      const [b, s] = await Promise.all([getBranch(id), getBranchStats(id)])
      setBranch(b)
      setStats(s)
    } catch (err) {
      console.error('[ERROR] Failed to load branch detail', err)
      showToast(t('admin.branch_detail.load_failed'), 'error')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { load() }, [load])

  function showToast(msg: string, type: 'success' | 'error') {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3500)
  }

  async function doAction(fn: () => Promise<unknown>, successMsg: string, errorMap?: Record<string, string>) {
    setActionLoading(true)
    try {
      await fn()
      await load()
      showToast(successMsg, 'success')
    } catch (err: any) {
      const code = err?.response?.data?.errorCode
      const msg = (errorMap && errorMap[code]) || err?.response?.data?.message || t('admin.branch_detail.action_failed')
      showToast(msg, 'error')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleSuspend() {
    if (!branch || !suspendReason.trim()) return
    await doAction(
      () => suspendBranch(branch.id, suspendReason.trim()),
      t('admin.branch_detail.suspended'),
      { BRANCH_NOT_ACTIVE: t('admin.branch_detail.not_active') }
    )
    setShowSuspendForm(false)
    setSuspendReason('')
  }

  async function handleArchive() {
    if (!branch || !confirm(t('admin.branch_detail.archive_confirm'))) return
    setActionLoading(true)
    try {
      await archiveBranch(branch.id)
      showToast(t('admin.branch_detail.archived'), 'success')
      setTimeout(() => navigate('/admin/branches'), 1200)
    } catch (err: any) {
      const code = err?.response?.data?.errorCode
      if (code === 'BRANCH_HAS_USERS') showToast(t('admin.branch_detail.has_users'), 'error')
      else showToast(t('admin.branch_detail.archive_failed'), 'error')
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}><div className="admin-spinner" /></div>
  }

  if (!branch) {
    return <div style={{ color: '#5a5d72', padding: 32 }}>{t('admin.branch_detail.not_found')}</div>
  }

  const isArchived = !!branch.archivedAt

  return (
    <div className="branch-detail">
      <div className="admin-page-header">
        <button className="admin-back-btn" onClick={() => navigate('/admin/branches')}>
          <BackIcon />
        </button>
        <h1 className="branch-detail__title">{branch.name}</h1>
        <BranchStatusBadge status={branch.status} />
      </div>

      {/* Actions */}
      {!isArchived && (
        <div className="branch-detail__actions">
          {branch.status === 'PENDING' && (
            <button
              className="admin-btn admin-btn--primary"
              disabled={actionLoading}
              onClick={() => doAction(() => approveBranch(branch.id), t('admin.branch_detail.approved'), {
                BRANCH_ALREADY_ACTIVE: t('admin.branch_detail.already_active'),
              })}
            >
              {t('admin.branch_detail.approve')}
            </button>
          )}
          {branch.status === 'ACTIVE' && !showSuspendForm && (
            <button
              className="admin-btn admin-btn--warn"
              disabled={actionLoading}
              onClick={() => setShowSuspendForm(true)}
            >
              {t('admin.branch_detail.suspend')}
            </button>
          )}
          {branch.status === 'SUSPENDED' && (
            <button
              className="admin-btn admin-btn--primary"
              disabled={actionLoading}
              onClick={() => doAction(() => reactivateBranch(branch.id), t('admin.branch_detail.reactivated'), {
                BRANCH_NOT_SUSPENDED: t('admin.branch_detail.not_suspended'),
              })}
            >
              {t('admin.branch_detail.reactivate')}
            </button>
          )}
          <button
            className="admin-btn admin-btn--secondary"
            disabled={actionLoading}
            onClick={() => setShowEdit(true)}
          >
            {t('admin.branch_detail.edit')}
          </button>
          <button
            className="admin-btn admin-btn--danger"
            disabled={actionLoading}
            onClick={handleArchive}
          >
            {t('admin.branch_detail.archive')}
          </button>
        </div>
      )}

      {/* Suspend form */}
      {showSuspendForm && (
        <div className="branch-detail__suspend-form">
          <input
            className="admin-input"
            placeholder={t('admin.branch_detail.suspend_placeholder')}
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleSuspend()}
          />
          <button className="admin-btn admin-btn--warn" onClick={handleSuspend} disabled={actionLoading || !suspendReason.trim()}>
            {t('admin.branch_detail.confirm_suspend')}
          </button>
          <button className="admin-btn admin-btn--secondary" onClick={() => { setShowSuspendForm(false); setSuspendReason('') }}>
            {t('admin.branch_detail.cancel')}
          </button>
        </div>
      )}

      {/* Info */}
      <div className="order-detail__grid-2">
        <div className="admin-detail-card">
          <p className="admin-detail-card__title">{t('admin.branch_detail.card_details')}</p>
          <div className="admin-detail-grid">
            <Field label={t('admin.branch_detail.field_name')} value={branch.name} />
            <Field label={t('admin.branch_detail.field_display_name')} value={branch.displayName} />
            <Field label={t('admin.branch_detail.field_address')} value={branch.address} />
            <Field label={t('admin.branch_detail.field_phone')} value={branch.phone} />
            <Field label={t('admin.branch_detail.field_email')} value={branch.email} />
            <Field label={t('admin.branch_detail.field_mute_video')} value={branch.muteReportVideo ? t('admin.branch_detail.mute_on') : t('admin.branch_detail.mute_off')} />
            <Field label={t('admin.branch_detail.field_locale')} value={branch.localeCode?.toUpperCase()} />
            <Field label={t('admin.branch_detail.field_created')} value={formatDate(branch.createdAt)} />
            {branch.suspendedAt && <Field label={t('admin.branch_detail.field_suspended_at')} value={formatDate(branch.suspendedAt)} />}
            {branch.suspendReason && <Field label={t('admin.branch_detail.field_suspend_reason')} value={branch.suspendReason} />}
            {branch.archivedAt && <Field label={t('admin.branch_detail.field_archived_at')} value={formatDate(branch.archivedAt)} />}
          </div>
        </div>

        {stats && (
          <div className="admin-detail-card">
            <p className="admin-detail-card__title">{t('admin.branch_detail.card_stats')}</p>
            <div className="branch-detail__stats">
              <StatBox label={t('admin.branch_detail.stat_total_orders')} value={stats.totalOrders} />
              <StatBox label={t('admin.branch_detail.stat_error_orders')} value={stats.errorOrders} warn={stats.errorOrders > 0} />
              <StatBox label={t('admin.branch_detail.stat_active_users')} value={stats.activeUsers} />
            </div>
          </div>
        )}
      </div>

      <BranchLogoCard branch={branch} readOnly={isArchived} onUpdated={setBranch} onToast={showToast} />

      <BranchSmsCard branchId={branch.id} readOnly={isArchived} onToast={showToast} />

      <DealerUsersCard branchId={branch.id} readOnly={isArchived} onToast={showToast} />

      {showEdit && (
        <BranchFormModal
          branch={branch}
          onClose={() => setShowEdit(false)}
          onSaved={(updated) => { setBranch(updated); setShowEdit(false); showToast(t('admin.branch_detail.updated'), 'success') }}
        />
      )}

      {toast && <div className={`admin-toast admin-toast--${toast.type}`}>{toast.msg}</div>}
    </div>
  )
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="admin-detail-field">
      <span className="admin-detail-field__label">{label}</span>
      <span className="admin-detail-field__value">{value || '—'}</span>
    </div>
  )
}

function StatBox({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className="branch-detail__stat-box">
      <span className="branch-detail__stat-value" style={warn && value > 0 ? { color: '#ef4444' } : undefined}>
        {value}
      </span>
      <span className="branch-detail__stat-label">{label}</span>
    </div>
  )
}

function BackIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M11 4L6 9L11 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
